import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Banner, Button, Card, IndexTable, Page, Select, Text, Thumbnail } from '@shopify/polaris';
import { ImageIcon, DeleteIcon } from '@shopify/polaris-icons';
import agent from '../../app/api/agent';
import { ProductDetails } from '../../app/models/Product';
import { useStore } from '../../app/stores/store';

type SelectOption = { label: string; value: string };

const sortFitments = (a: ProductDetails['fitments'][number], b: ProductDetails['fitments'][number]) => {
    const yearCompare = Number(b.year) - Number(a.year);
    if (yearCompare !== 0) {
        return yearCompare;
    }

    const makeCompare = a.make.localeCompare(b.make);
    if (makeCompare !== 0) {
        return makeCompare;
    }

    return a.model.localeCompare(b.model);
};

const DEFAULT_YEAR_OPTIONS: SelectOption[] = [{ label: 'Select year', value: '' }];
const DEFAULT_MAKE_OPTIONS: SelectOption[] = [{ label: 'Select make', value: '' }];
const DEFAULT_MODEL_OPTIONS: SelectOption[] = [{ label: 'Select model', value: '' }];

const ProductDetailsPage: React.FC = () => {
    const { id } = useParams();
    const productId = Number(id);
    const navigate = useNavigate();
    const { userStore } = useStore();

    const [product, setProduct] = useState<ProductDetails | null>(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string>('');

    const [selectedYear, setSelectedYear] = useState<string>('');
    const [selectedMake, setSelectedMake] = useState<string>('');
    const [selectedModel, setSelectedModel] = useState<string>('');
    const [yearOptions, setYearOptions] = useState<SelectOption[]>(DEFAULT_YEAR_OPTIONS);
    const [makeOptions, setMakeOptions] = useState<SelectOption[]>(DEFAULT_MAKE_OPTIONS);
    const [modelOptions, setModelOptions] = useState<SelectOption[]>(DEFAULT_MODEL_OPTIONS);
    const [loadingYears, setLoadingYears] = useState(false);
    const [loadingMakes, setLoadingMakes] = useState(false);
    const [loadingModels, setLoadingModels] = useState(false);

    const loadDetails = useCallback(async () => {
        if (!productId || Number.isNaN(productId)) {
            setError('Invalid product id.');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const result = await agent.Products.details(productId);
            setProduct(result);
        } catch (e: any) {
            setError(e?.message ?? 'Failed to load product details.');
        } finally {
            setLoading(false);
        }
    }, [productId]);

    useEffect(() => {
        loadDetails();
    }, [loadDetails]);

    useEffect(() => {
        const loadYears = async () => {
            setLoadingYears(true);
            try {
                const result = await agent.Vehicles.filterOptions();
                setYearOptions([...DEFAULT_YEAR_OPTIONS, ...result.years.map((year) => ({ label: year, value: year }))]);
            } catch {
                setYearOptions(DEFAULT_YEAR_OPTIONS);
            } finally {
                setLoadingYears(false);
            }
        };

        loadYears();
    }, []);

    useEffect(() => {
        if (!selectedYear) {
            setMakeOptions(DEFAULT_MAKE_OPTIONS);
            setLoadingMakes(false);
            return;
        }

        const loadMakes = async () => {
            setLoadingMakes(true);
            try {
                const result = await agent.Vehicles.filterOptions(selectedYear);
                setMakeOptions([...DEFAULT_MAKE_OPTIONS, ...result.makes.map((make) => ({ label: make, value: make }))]);
            } catch {
                setMakeOptions(DEFAULT_MAKE_OPTIONS);
            } finally {
                setLoadingMakes(false);
            }
        };

        loadMakes();
    }, [selectedYear]);

    useEffect(() => {
        if (!selectedYear || !selectedMake) {
            setModelOptions(DEFAULT_MODEL_OPTIONS);
            setLoadingModels(false);
            return;
        }

        const loadModels = async () => {
            setLoadingModels(true);
            try {
                const result = await agent.Vehicles.filterOptions(selectedYear, selectedMake);
                setModelOptions([...DEFAULT_MODEL_OPTIONS, ...result.models.map((model) => ({ label: model, value: model }))]);
            } catch {
                setModelOptions(DEFAULT_MODEL_OPTIONS);
            } finally {
                setLoadingModels(false);
            }
        };

        loadModels();
    }, [selectedYear, selectedMake]);

    const onAddFitment = useCallback(async () => {
        if (!product || !selectedYear || !selectedMake || !selectedModel) return;

        setSubmitting(true);
        setError('');
        try {
            await agent.Products.addFitment(product.id, {
                year: selectedYear,
                make: selectedMake,
                model: selectedModel,
            });
            setSelectedYear('');
            setSelectedMake('');
            setSelectedModel('');
            await loadDetails();
        } catch (e: any) {
            setError(e?.message ?? 'Failed to add fitment.');
        } finally {
            setSubmitting(false);
        }
    }, [product, selectedYear, selectedMake, selectedModel, loadDetails]);

    const onRemoveFitment = useCallback(async (fitmentId: number) => {
        if (!product) return;

        setSubmitting(true);
        setError('');
        try {
            await agent.Products.removeFitment(product.id, fitmentId);
            await loadDetails();
        } catch (e: any) {
            setError(e?.message ?? 'Failed to remove fitment.');
        } finally {
            setSubmitting(false);
        }
    }, [product, loadDetails]);

    const sortedFitments = [...(product?.fitments || [])].sort(sortFitments);
    const shopHandle = userStore.user?.shopDomain?.replace('.myshopify.com', '') ?? '';
    const shopifyAdminProductUrl = shopHandle && product?.id
        ? `https://admin.shopify.com/store/${shopHandle}/products/${product.id}`
        : '';

    return (
        <Page
            fullWidth
            title={product?.title || 'Product Details'}
            backAction={{ content: 'Products', onAction: () => navigate('/products') }}
        >
            {error && (
                <div style={{ marginBottom: 12 }}>
                    <Banner title="Error" tone="critical">{error}</Banner>
                </div>
            )}

            <Card>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    {product?.featuredImage ? (
                        <Thumbnail source={product.featuredImage} alt={product.title} size="large" />
                    ) : (
                        <Thumbnail source={ImageIcon} alt={product?.title ?? 'Product image'} size="large" />
                    )}
                    <div>
                        <Text as="h2" variant="headingMd">{product?.title || '-'}</Text>
                        <Text as="p" variant="bodyMd">SKU: {product?.sku || '-'}</Text>
                        <Text as="p" variant="bodyMd">Vendor: {product?.vendor || '-'}</Text>
                        <Text as="p" variant="bodyMd">Fitments: {product?.fitmentCount || 0}</Text>
                        {shopifyAdminProductUrl && (
                            <div style={{ marginTop: 8 }}>
                                <Button url={shopifyAdminProductUrl} target="_blank" variant="plain">
                                    View in Shopify admin
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </Card>

            <div style={{ marginTop: 16 }}>
                <Card>
                    <Text as="h3" variant="headingMd">Add fitment</Text>
                    <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
                        <div style={{ flex: 1 }}>
                            <Select label="Year" options={yearOptions} value={selectedYear} disabled={loadingYears} onChange={(value) => { setSelectedYear(value); setSelectedMake(''); setSelectedModel(''); }} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <Select label="Make" options={makeOptions} value={selectedMake} disabled={loadingYears || loadingMakes || !selectedYear} onChange={(value) => { setSelectedMake(value); setSelectedModel(''); }} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <Select label="Model" options={modelOptions} value={selectedModel} disabled={loadingYears || loadingMakes || loadingModels || !selectedYear || !selectedMake} onChange={setSelectedModel} />
                        </div>
                        <div style={{ alignSelf: 'end' }}>
                            <Button variant="primary" onClick={onAddFitment} loading={submitting || loading} disabled={!selectedYear || !selectedMake || !selectedModel}>Add</Button>
                        </div>
                    </div>
                </Card>
            </div>

            <div style={{ marginTop: 16 }}>
                <Card>
                    <IndexTable
                        resourceName={{ singular: 'fitment', plural: 'fitments' }}
                        itemCount={sortedFitments.length}
                        headings={[
                            { title: 'Year' },
                            { title: 'Make' },
                            { title: 'Model' },
                            { title: 'Type' },
                            { title: 'Source' },
                            { title: '' },
                        ]}
                        selectable={false}
                    >
                        {sortedFitments.map((fitment, index) => (
                            <IndexTable.Row id={String(fitment.id)} key={fitment.id} position={index}>
                                <IndexTable.Cell>{fitment.year}</IndexTable.Cell>
                                <IndexTable.Cell>{fitment.make}</IndexTable.Cell>
                                <IndexTable.Cell>{fitment.model}</IndexTable.Cell>
                                <IndexTable.Cell>{fitment.sourceType}</IndexTable.Cell>
                                <IndexTable.Cell>{fitment.source || '-'}</IndexTable.Cell>
                                <IndexTable.Cell>
                                    <Button icon={DeleteIcon} variant="tertiary" tone="critical" onClick={() => onRemoveFitment(fitment.id)} loading={submitting || loading}>Remove</Button>
                                </IndexTable.Cell>
                            </IndexTable.Row>
                        ))}
                    </IndexTable>
                </Card>
            </div>
        </Page>
    );
};

export default ProductDetailsPage;
